import type { OperationVariables } from '@apollo/client'
import type { SkipToken } from '@apollo/client/react/compiled'
import { skipToken, useSuspenseQuery as useBaseSuspenseQuery } from '@apollo/client/react/compiled'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import useUser from '@hooks/useUser'

import { getPermissionVariables } from './permissionVariables'

export type UseSuspenseQueryResult<
  TData,
  TVariables extends OperationVariables,
> = useBaseSuspenseQuery.Result<TData, TVariables, 'complete'>

type UseSuspenseQueryOpts<TVariables extends OperationVariables> = Partial<
  Omit<useBaseSuspenseQuery.Options<TVariables>, 'variables'> & {
    variables: TVariables
  }
>

export function useSuspenseQuery<TData, TVariables extends OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  opts: UseSuspenseQueryOpts<TVariables> | SkipToken,
): UseSuspenseQueryResult<TData, TVariables> {
  // Same PERMISSION_* injection as `useQuery` — permission-gated `@include`
  // fragment fields must resolve identically whichever read hook fetched them.
  // (Hook order: useUser must run unconditionally, skipToken or not.)
  const { user } = useUser()

  const resolvedOpts =
    opts === skipToken
      ? opts
      : {
          ...opts,
          variables: {
            ...opts.variables,
            ...getPermissionVariables(user?.accountPermissions),
          },
        }

  // AC 4.2 modern signatures are inference-only (no explicit generics) and
  // their conditional options tuple doesn't resolve for an unbound generic
  // TVariables, so widen to OperationVariables at the call and narrow the
  // result back to the wrapper's stable public type. Same as `useQuery`.
  return useBaseSuspenseQuery(
    document as TypedDocumentNode<TData, OperationVariables>,
    resolvedOpts as useBaseSuspenseQuery.Options<OperationVariables>,
  ) as UseSuspenseQueryResult<TData, TVariables>
}
